import React, { useEffect, useState } from 'react';
import {
  Container, Typography, Grid, Paper, Box, CircularProgress, Chip, Alert, LinearProgress, Button
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import StorageIcon from '@mui/icons-material/Storage';
import MemoryIcon from '@mui/icons-material/Memory';
import MiscellaneousServicesIcon from '@mui/icons-material/MiscellaneousServices';
import CloudIcon from '@mui/icons-material/Cloud';
import FolderIcon from '@mui/icons-material/Folder';
import SecurityIcon from '@mui/icons-material/Security';
import ViewInArIcon from '@mui/icons-material/ViewInAr';
import SdStorageIcon from '@mui/icons-material/SdStorage';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import ChatIcon from '@mui/icons-material/Chat';
import { swarmApi, certsApi } from '../api';
import { LiveEventPanel } from './LiveEventFeed';

function StatCard({ title, value, icon, color }) {
  return (
    <Paper sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
      <Box sx={{ bgcolor: `${color}.light`, borderRadius: 2, p: 1.5, display: 'flex' }}>
        {icon}
      </Box>
      <Box>
        <Typography variant="h4" fontWeight="bold">{value}</Typography>
        <Typography variant="body2" color="text.secondary">{title}</Typography>
      </Box>
    </Paper>
  );
}

function formatBytes(b) {
  if (!b || b === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(b) / Math.log(1024));
  return `${(b / Math.pow(1024, i)).toFixed(1)} ${units[i] || 'B'}`;
}

function DiskBar({ label, count, bytes, total, color }) {
  const pct = total > 0 ? Math.min((bytes || 0) / total * 100, 100) : 0;
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography variant="body2"><strong>{label}</strong> ({count ?? 0})</Typography>
        <Typography variant="body2" color="text.secondary">{formatBytes(bytes)}</Typography>
      </Box>
      <LinearProgress variant="determinate" value={pct} color={color} sx={{ height: 8, borderRadius: 4 }} />
    </Box>
  );
}

function Dashboard() {
  const [info, setInfo] = useState(null);
  const [services, setServices] = useState([]);
  const [volumes, setVolumes] = useState([]);
  const [containers, setContainers] = useState([]);
  const [certs, setCerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [diskUsage, setDiskUsage] = useState(null);
  const [aiPending, setAiPending] = useState(null);
  const [recentIntents, setRecentIntents] = useState([]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [infoRes, servicesRes, volumeRes, certsRes, containersRes, diskRes, pendingRes, intentsRes] = await Promise.all([
        swarmApi.getInfo(),
        swarmApi.listServices().catch(() => ({ data: [] })),
        swarmApi.listVolumes(),
        certsApi.list().catch(() => ({ data: [] })),
        swarmApi.listContainers(true).catch(() => ({ data: [] })),
        swarmApi.getDiskUsage().catch(() => ({ data: null })),
        swarmApi.getIntentPendingCount().catch(() => ({ data: null })),
        swarmApi.listIntents(5).catch(() => ({ data: [] })),
      ]);
      setInfo(infoRes.data);
      setServices(servicesRes.data);
      setVolumes(volumeRes.data);
      setCerts(certsRes.data);
      setContainers(containersRes.data);
      setDiskUsage(diskRes.data);
      setAiPending(pendingRes.data);
      setRecentIntents(Array.isArray(intentsRes.data) ? intentsRes.data : []);
      setError('');
    } catch (err) {
      setError('Failed to fetch dashboard data. Is the gateway connected to Docker?');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 15000);
    return () => clearInterval(interval);
  }, []);

  if (loading && !info) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
        <CircularProgress />
      </Container>
    );
  }

  const memGB = info ? (info.memoryBytes / 1073741824).toFixed(1) : 0;
  const runningServices = services.filter(s => s.status === 'running').length;
  const degradedServices = services.filter(s => s.status === 'degraded').length;
  const runningContainers = containers.filter(c => c.state === 'running').length;
  const exitedContainers = containers.filter(c => c.state === 'exited').length;

  return (
    <Container maxWidth="xl">
      <Typography variant="h4" fontWeight="bold" gutterBottom>Dashboard</Typography>
      {error && <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert>}

      {aiPending?.totalAttention > 0 && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" component={RouterLink} to="/intent">
              Review
            </Button>
          }
        >
          AI needs attention: {aiPending.awaitingApproval || 0} intent(s) awaiting approval,
          {' '}{aiPending.recommendationsPending || 0} recommendation(s) pending.
        </Alert>
      )}

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Services Running" value={runningServices} icon={<MiscellaneousServicesIcon color="primary" />} color="primary" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="AI Pending Approvals"
            value={aiPending?.totalAttention ?? '—'}
            icon={<SmartToyIcon color="secondary" />}
            color="secondary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Total Services" value={info?.totalServices || services.length} icon={<StorageIcon color="secondary" />} color="secondary" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Volumes" value={info?.totalVolumes || volumes.length} icon={<FolderIcon color="success" />} color="success" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Containers Running" value={`${runningContainers}/${containers.length}`} icon={<ViewInArIcon color="warning" />} color="warning" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Swarm Active" value={info?.swarmActive ? 'Yes' : 'No'} icon={<CloudIcon color="info" />} color="info" />
        </Grid>
        {diskUsage && (
          <Grid item xs={12} sm={6} md={3}>
            <StatCard title="Disk Usage" value={formatBytes(diskUsage.totalBytes)} icon={<SdStorageIcon color="error" />} color="error" />
          </Grid>
        )}
      </Grid>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>System Info</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Typography variant="body2"><strong>Host:</strong> {info?.name}</Typography>
              <Typography variant="body2"><strong>OS:</strong> {info?.operatingSystem}</Typography>
              <Typography variant="body2"><strong>Architecture:</strong> {info?.architecture}</Typography>
              <Typography variant="body2"><strong>Docker:</strong> {info?.serverVersion}</Typography>
              <Typography variant="body2"><strong>CPUs:</strong> {info?.ncpu}</Typography>
              <Typography variant="body2"><strong>Memory:</strong> {memGB} GB</Typography>
              <Typography variant="body2"><strong>Images:</strong> {info?.images}</Typography>
              <Typography variant="body2"><strong>Containers:</strong> {info?.containersRunning} running / {info?.containers} total</Typography>
              {info?.swarmActive && (
                <>
                  <Typography variant="body2"><strong>Swarm Nodes:</strong> {info?.swarmNodes}</Typography>
                  <Typography variant="body2"><strong>Swarm Managers:</strong> {info?.swarmManagers}</Typography>
                </>
              )}
            </Box>
          </Paper>
        </Grid>
        {diskUsage && (
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                <SdStorageIcon sx={{ verticalAlign: 'middle', mr: 0.5 }} fontSize="small" />
                Disk Usage
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <DiskBar label="Images" count={diskUsage.images?.count} bytes={diskUsage.images?.sizeBytes} total={diskUsage.totalBytes} color="primary" />
                <DiskBar label="Containers" count={diskUsage.containers?.count} bytes={diskUsage.containers?.sizeBytes} total={diskUsage.totalBytes} color="warning" />
                <DiskBar label="Volumes" count={diskUsage.volumes?.count} bytes={diskUsage.volumes?.sizeBytes} total={diskUsage.totalBytes} color="success" />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>Total: {formatBytes(diskUsage.totalBytes)}</Typography>
              </Box>
            </Paper>
          </Grid>
        )}
        <Grid item xs={12} md={6}>
          <LiveEventPanel />
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <ChatIcon sx={{ mr: 1 }} fontSize="small" color="primary" />
              <Typography variant="h6" sx={{ flexGrow: 1 }}>Recent AI intents</Typography>
              <Button size="small" component={RouterLink} to="/intent">Open console</Button>
            </Box>
            {recentIntents.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No intents yet. Use the Intent Console to manage the swarm in plain English.
              </Typography>
            ) : (
              <Box sx={{ maxHeight: 220, overflow: 'auto' }}>
                {recentIntents.map((i) => (
                  <Box key={i.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.75, borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Chip
                      size="small"
                      label={i.status}
                      color={i.status === 'completed' ? 'success' : i.status === 'awaiting_approval' ? 'warning' : i.status === 'failed' ? 'error' : 'default'}
                    />
                    <Typography variant="body2" sx={{ flexGrow: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      #{i.id} {i.summary || i.message}
                    </Typography>
                    <Chip size="small" variant="outlined" label={i.maxRisk} />
                  </Box>
                ))}
              </Box>
            )}
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>Services</Typography>
            <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
              {services.map(s => (
                <Box key={s.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                  <Chip
                    label={s.status}
                    size="small"
                    color={s.status === 'running' ? 'success' : s.status === 'degraded' ? 'warning' : 'error'}
                  />
                  <Typography variant="body2" sx={{ flexGrow: 1 }}>{s.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {s.runningReplicas}/{s.desiredReplicas} replicas
                  </Typography>
                </Box>
              ))}
              {services.length === 0 && <Typography variant="body2" color="text.secondary">No services found</Typography>}
            </Box>
          </Paper>
        </Grid>
      </Grid>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              <ViewInArIcon sx={{ verticalAlign: 'middle', mr: 0.5 }} fontSize="small" />
              Containers ({containers.length})
            </Typography>
            <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
              {containers.filter(c => c.state === 'running').map(c => (
                <Box key={c.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                  <Chip
                    label={c.state}
                    size="small"
                    color="success"
                  />
                  <Typography variant="body2" sx={{ flexGrow: 1 }} noWrap>{c.name}</Typography>
                  <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 200 }}>
                    {c.image?.split('@')[0]}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">{c.status}</Typography>
                </Box>
              ))}
              {exitedContainers > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    + {exitedContainers} exited container{exitedContainers !== 1 ? 's' : ''}
                  </Typography>
                </Box>
              )}
              {containers.length === 0 && <Typography variant="body2" color="text.secondary">No containers found</Typography>}
            </Box>
          </Paper>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>Volumes ({volumes.length})</Typography>
            <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
              {volumes.map(v => (
                <Box key={v.name} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="body2" sx={{ flexGrow: 1 }}>{v.name}</Typography>
                  <Box>
                    {v.usedByServices?.length > 0
                      ? v.usedByServices.map((svc, i) => <Chip key={i} label={svc} size="small" variant="outlined" sx={{ mr: 0.5 }} />)
                      : <Typography variant="caption" color="text.secondary">Unused</Typography>
                    }
                  </Box>
                  <Typography variant="caption" color="text.secondary">Driver: {v.driver}</Typography>
                </Box>
              ))}
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              <SecurityIcon sx={{ verticalAlign: 'middle', mr: 0.5 }} fontSize="small" />
              SSL Certificates
            </Typography>
            {certs.length > 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {certs.map((c, i) => {
                  const expiryValue = c.expiry || c.expiresAt || null;
                  const parsedDays = Number.isFinite(c.daysUntilExpiry)
                    ? c.daysUntilExpiry
                    : (expiryValue ? Math.floor((new Date(expiryValue) - new Date()) / 86400000) : null);
                  const color = parsedDays == null
                    ? 'default'
                    : parsedDays > 30
                      ? 'success'
                      : parsedDays > 7
                        ? 'warning'
                        : 'error';
                  const label = parsedDays == null ? 'Unknown' : parsedDays > 0 ? `${parsedDays}d` : 'Expired';
                  return (
                    <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                      <Chip label={label} size="small" color={color} />
                      <Typography variant="body2" sx={{ flexGrow: 1 }}>{c.domain}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {expiryValue ? new Date(expiryValue).toLocaleDateString() : '-'}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">No certificates found</Typography>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
}
export default Dashboard;
